import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Button,
  TextField,
  Tooltip,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ReactComponent as CloseIcon } from '../../../assets/SVG/close-circle-greay.svg';
import {
  filterInputSubLabelSx,
  HeaderSx,
  modalCloseIconSx,
  modalCloseSx,
  modalSaveSx,
} from '../../Inventory/Common/inventory.helper';

// Validation schema using Yup
export const rejectSalesReturnValidationSchema = Yup.object().shape({
  rejectRemark: Yup.string()
    .required('Reject remark is required')
    .min(5, 'Reject remark must be at least 5 characters')
    .max(500, 'Reject remark must not exceed 500 characters'),
});

export interface RejectSalesReturnValues {
  rejectRemark: string;
}

interface RejectSalesReturnModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: RejectSalesReturnValues) => void;
  initialValues?: RejectSalesReturnValues;
}

const boxContainerSx = {
  width: 480,
  bgcolor: 'background.paper',
  borderRadius: 4,
  mx: 'auto',
  mt: '20%',
  boxShadow: 24,
  outline: 'none',
  overflow: 'hidden',
};

const RejectSalesReturnModal: React.FC<RejectSalesReturnModalProps> = ({
  open,
  onClose,
  onSave,
  initialValues = {
    rejectRemark: '',
  },
}) => {
  const { t } = useTranslation('sales_return');
  return (
    <Modal open={open} onClose={onClose}>
      <Box data-testid="reject-sales-return-modal" sx={boxContainerSx}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          px={2}
          mb={2}
          borderBottom={'1px solid rgba(226, 227, 229, 1)'}
        >
          <Typography sx={HeaderSx}>Reject Sales Return</Typography>
          <Tooltip title={t('Tooltip_Close')} arrow>
            <IconButton onClick={onClose} sx={modalCloseIconSx}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Formik
          initialValues={initialValues}
          validationSchema={rejectSalesReturnValidationSchema}
          onSubmit={(values) => {
            onSave(values);
            onClose();
          }}
        >
          {({ values, handleChange, errors, touched }) => (
            <Form>
              <Box p={3} pt={1}>
                <Box mb={3}>
                  <TextField
                    name="rejectRemark"
                    label={t('Reject_Remark')}
                    placeholder={t('Reject_Reason_Placeholder')}
                    type="text"
                    fullWidth
                    multiline
                    rows={2}
                    value={values.rejectRemark}
                    onChange={handleChange}
                    sx={filterInputSubLabelSx}
                    error={touched.rejectRemark && !!errors.rejectRemark}
                    helperText={touched.rejectRemark && errors.rejectRemark}
                  />
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                display="flex"
                justifyContent="space-between"
                gap={2}
                p={3}
                pt={0}
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={modalCloseSx}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  sx={modalSaveSx}
                >
                  Submit
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default RejectSalesReturnModal;
